import { Injectable, inject } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { parseProductPrice } from '../../models/billing-product.type';
import { isBillDueDate } from '../../models/patient-bill.type';
import { PatientPayment } from '../../models/patient-payment.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { FirebaseAuthService } from '../auth/firebase-auth.service';
import { MembershipService } from '../auth/membership.service';
import { UserProfileService } from '../auth/user-profile.service';
import { PatientBalanceService } from './patient-balance.service';

@Injectable({ providedIn: 'root' })
export class PatientPaymentService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private auth = inject(FirebaseAuthService);
  private membership = inject(MembershipService);
  private profiles = inject(UserProfileService);
  private balances = inject(PatientBalanceService);

  async listPayments(patientId: string): Promise<PatientPayment[]> {
    const snap = await getDocs(this.paymentsCollection(patientId));
    return snap.docs
      .map((paymentDoc) => parsePatientPayment(paymentDoc.id, paymentDoc.data()))
      .filter((payment): payment is PatientPayment => payment != null)
      .sort((a, b) => b.paidOn.localeCompare(a.paidOn) || b.id.localeCompare(a.id));
  }

  async createPayment(input: {
    patientId: string;
    amount: number;
    paidOn: string;
  }): Promise<string> {
    const uid = this.auth.uniqueId();
    if (!uid) {
      throw new Error('Not signed in');
    }
    this.assertCanRecord();
    const amount = parseProductPrice(input.amount);
    if (amount == null || amount <= 0) {
      throw new Error('El monto no es válido.');
    }
    if (!isBillDueDate(input.paidOn)) {
      throw new Error('La fecha no es válida.');
    }

    const registeredByName = this.registrarName();
    const paymentRef = doc(this.paymentsCollection(input.patientId));
    await setDoc(paymentRef, {
      patientId: input.patientId,
      amount,
      paidOn: input.paidOn,
      createdBy: uid,
      registeredByName,
      createdAt: serverTimestamp(),
    });
    await this.syncAmountOwed(input.patientId);
    return paymentRef.id;
  }

  async deletePayment(patientId: string, paymentId: string): Promise<void> {
    this.assertCanRecord();
    await deleteDoc(this.paymentDoc(patientId, paymentId));
    await this.syncAmountOwed(patientId);
  }

  /** Label for the signed-in user, matching the app bar. */
  registrarName(): string {
    const label = this.profiles.doctorLabel().trim();
    if (label) {
      return label.slice(0, 120);
    }
    const email = this.auth.user()?.email?.trim();
    if (email) {
      return email.slice(0, 120);
    }
    throw new Error('No se pudo identificar quién registra el pago.');
  }

  private async syncAmountOwed(patientId: string): Promise<void> {
    try {
      await this.balances.recomputeAmountOwed(patientId);
    } catch (err) {
      console.error('[PatientPayment] balance sync failed', err);
    }
  }

  private assertCanRecord(): void {
    if (!this.membership.canManageClinicalRecords()) {
      throw new Error('No tiene permiso para registrar pagos.');
    }
  }

  private paymentsCollection(patientId: string) {
    const businessId = this.membership.requireSelectedBusinessId();
    return collection(
      this.firestore,
      'businesses',
      businessId,
      'patients',
      patientId,
      'payments'
    );
  }

  private paymentDoc(patientId: string, paymentId: string) {
    const businessId = this.membership.requireSelectedBusinessId();
    return doc(
      this.firestore,
      'businesses',
      businessId,
      'patients',
      patientId,
      'payments',
      paymentId
    );
  }
}

function parsePatientPayment(id: string, data: Record<string, unknown>): PatientPayment | null {
  const patientId = typeof data['patientId'] === 'string' ? data['patientId'] : '';
  const amount = typeof data['amount'] === 'number' ? parseProductPrice(data['amount']) : null;
  const paidOn = typeof data['paidOn'] === 'string' ? data['paidOn'] : '';
  const createdBy = typeof data['createdBy'] === 'string' ? data['createdBy'] : '';
  const registeredByName =
    typeof data['registeredByName'] === 'string' ? data['registeredByName'].trim() : '';
  if (!patientId || amount == null || amount <= 0 || !isBillDueDate(paidOn)) {
    return null;
  }
  return {
    id,
    patientId,
    amount,
    paidOn,
    createdBy,
    registeredByName: registeredByName || undefined,
  };
}
